import type { ReactNode } from "react";
import { CheckSquare, Square } from "lucide-react";
import { cn } from "@/lib/utils";
import { Pagination } from "./Pagination";
import { LoadingState } from "./LoadingState";

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  align?: "left" | "center" | "right";
  width?: string;
  className?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  onRowClick?: (row: T) => void;
  /** Lignes sélectionnées (par `rowKey`). Active la colonne checkbox. */
  selected?: (string | number)[];
  onToggleSelect?: (row: T) => void;
  onToggleSelectAll?: () => void;
  /** Slot d'actions affichées en fin de ligne, toujours visibles (pas de survol sur tablette). */
  rowActions?: (row: T) => ReactNode;
  empty?: ReactNode;
  emptyTitle?: string;
  loading?: boolean;
  pagination?: {
    page: number;
    totalPages: number;
    total?: number;
    pageSize?: number;
    onChange: (page: number) => void;
  };
  className?: string;
  /** Hauteur max pour le scroll vertical du `<tbody>`. Défaut : 65vh. */
  maxHeight?: string;
  minHeight?: string;
}

const ALIGN_CLASS = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  selected,
  onToggleSelect,
  onToggleSelectAll,
  rowActions,
  empty,
  emptyTitle = "Aucun résultat",
  loading,
  pagination,
  className,
  maxHeight = "65vh",
  minHeight,
}: DataTableProps<T>) {
  const showSelection = !!selected && !!onToggleSelect;
  const allSelected = showSelection && selected!.length === rows.length && rows.length > 0;
  const showEmpty = !loading && rows.length === 0;
  const colSpan = columns.length + (showSelection ? 1 : 0) + (rowActions ? 1 : 0);

  return (
    <div className={cn("card-surface overflow-hidden", className)}>
      <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight, minHeight }}>
        <table className="w-full border-collapse text-left">
          <thead className="sticky top-0 z-10 bg-gray-50/50 backdrop-blur">
            <tr>
              {showSelection && (
                <th className="w-10 px-5 py-5">
                  <button
                    type="button"
                    onClick={onToggleSelectAll}
                    className="rounded-lg p-1 text-indigo-600 transition-all hover:bg-white"
                  >
                    {allSelected ? (
                      <CheckSquare size={20} />
                    ) : (
                      <Square size={20} className="text-gray-300" />
                    )}
                  </button>
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  style={col.width ? { width: col.width } : undefined}
                  className={cn(
                    "px-5 py-5 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400",
                    ALIGN_CLASS[col.align ?? "left"],
                  )}
                >
                  {col.header}
                </th>
              ))}
              {rowActions && (
                <th className="px-5 py-5 text-right text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {loading && (
              <tr>
                <td colSpan={colSpan} className="px-5 py-12">
                  <LoadingState />
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((row) => {
                const key = rowKey(row);
                const isSelected = showSelection ? selected!.includes(key) : false;
                return (
                  <tr
                    key={key}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={cn(
                      "group transition-colors",
                      isSelected ? "bg-indigo-50/20" : "hover:bg-indigo-50/10",
                      onRowClick && "cursor-pointer",
                    )}
                  >
                    {showSelection && (
                      <td className="px-5 py-5">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleSelect!(row);
                          }}
                          className="rounded-lg p-1 text-indigo-600"
                        >
                          {isSelected ? (
                            <CheckSquare size={20} />
                          ) : (
                            <Square size={20} className="text-gray-200" />
                          )}
                        </button>
                      </td>
                    )}
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={cn(
                          "px-5 py-5 text-sm text-gray-700",
                          ALIGN_CLASS[col.align ?? "left"],
                          col.className,
                        )}
                      >
                        {col.cell(row)}
                      </td>
                    ))}
                    {rowActions && (
                      <td className="px-5 py-5 text-right">
                        <div className="flex items-center justify-end gap-1">{rowActions(row)}</div>
                      </td>
                    )}
                  </tr>
                );
              })}
            {showEmpty && (
              <tr>
                <td colSpan={colSpan} className="px-5 py-12">
                  {empty ?? (
                    <p className="text-center text-[10px] font-black uppercase tracking-widest text-gray-400">
                      {emptyTitle}
                    </p>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {pagination && (
        <div className="px-5 py-4">
          <Pagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            pageSize={pagination.pageSize}
            onChange={pagination.onChange}
          />
        </div>
      )}
    </div>
  );
}
